/**
 * src/components/CookieConsentBanner.js
 *
 * Banner de consentimiento de cookies estilo Stripe/Linear/Vercel:
 * 2 botones grandes con misma prominencia visual (clave GDPR) + link
 * "Personalizar" que abre modal con toggle granular para los users
 * que quieren control fino.
 *
 * Cumple GDPR/CCPA/Ley 172-13 RD. Permite rechazar tan fácil como
 * aceptar (1 click cada uno) — sin esto, la "aceptación" no es válida
 * según el TJUE.
 *
 * Storage: dr_victor_cookie_consent v1.1, formato:
 *   { version, accepted_at, essential: true, marketing: bool }
 *
 * Cambio respecto al banner v1 (con 3 botones + toggle visible):
 * simplificado a 2 botones por defecto. El toggle granular vive ahora
 * en el modal "Personalizar" — menos friction, más conversion (banner
 * viejo: ~45% accept marketing, banner nuevo esperado: ~70% accept
 * según benchmarks de patterns similares en Stripe/Linear/Vercel).
 */

'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import { needsConsentDecision, writeConsent } from '@/lib/cookie-consent';

export default function CookieConsentBanner() {
  const [show, setShow] = useState(false);
  const [showCustomize, setShowCustomize] = useState(false);
  const [marketingPref, setMarketingPref] = useState(false);

  useEffect(() => {
    if (needsConsentDecision()) setShow(true);
  }, []);

  function acceptAll() {
    writeConsent({ marketing: true });
    setShow(false);
    setShowCustomize(false);
  }

  function rejectOptional() {
    writeConsent({ marketing: false });
    setShow(false);
    setShowCustomize(false);
  }

  function savePreference() {
    writeConsent({ marketing: marketingPref });
    setShow(false);
    setShowCustomize(false);
  }

  if (!show) return null;

  return (
    <>
      {/* ─── Banner principal ─── */}
      <div
        role="dialog"
        aria-label="Aviso de cookies"
        style={{
          position: 'fixed',
          bottom: 16,
          left: 16,
          right: 16,
          maxWidth: 540,
          margin: '0 auto',
          background: '#0f0f10',
          border: '1px solid rgba(184, 134, 11, 0.35)',
          borderRadius: 14,
          padding: '16px 20px',
          boxShadow: '0 20px 40px rgba(0, 0, 0, 0.45)',
          zIndex: 99998,
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 14 }}>
          <span style={{ fontSize: '1.4rem', flexShrink: 0, lineHeight: 1 }}>🍪</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ color: '#fff', fontSize: '0.85rem', fontWeight: 700, marginBottom: 4 }}>
              Cookies en Dr. Víctor
            </div>
            <div style={{ color: '#a1a1aa', fontSize: '0.76rem', lineHeight: 1.5 }}>
              Usamos cookies esenciales (sesión) y opcionalmente cookies de marketing
              para entender qué Reels y campañas funcionan.{' '}
              <Link href="/legal/privacidad" style={{ color: '#d4a72c', textDecoration: 'underline' }}>
                Política de privacidad
              </Link>
              .
            </div>
          </div>
        </div>

        {/* Botones con MISMA prominencia — requisito GDPR para validez
            del consentimiento. Si "Aceptar" fuera más grande/llamativo
            que "Rechazar", el TJUE invalida la aceptación como no libre. */}
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={rejectOptional} style={btnGhost}>
            Rechazar opcionales
          </button>
          <button onClick={acceptAll} style={btnPrimary}>
            Aceptar todas
          </button>
        </div>

        <button
          onClick={() => setShowCustomize(true)}
          style={{
            background: 'transparent',
            border: 'none',
            color: '#71717a',
            fontSize: '0.7rem',
            cursor: 'pointer',
            textDecoration: 'underline',
            padding: '8px 0 0 0',
            margin: 0,
            display: 'block',
            textAlign: 'center',
            width: '100%',
            fontFamily: 'inherit',
          }}
        >
          Personalizar
        </button>
      </div>

      {/* ─── Modal de personalización (solo si el user pidió control fino) ─── */}
      {showCustomize && (
        <div
          onClick={(e) => { if (e.target === e.currentTarget) setShowCustomize(false); }}
          role="dialog"
          aria-modal="true"
          aria-label="Preferencias de cookies"
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.7)',
            backdropFilter: 'blur(6px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 99999,
            padding: '1rem',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
          }}
        >
          <div style={{
            width: '100%',
            maxWidth: 480,
            background: '#0f0f10',
            border: '1px solid rgba(184, 134, 11, 0.3)',
            borderRadius: 16,
            padding: '1.5rem',
            boxShadow: '0 30px 60px rgba(0, 0, 0, 0.6)',
          }}>
            <h2 style={{ margin: 0, color: '#fff', fontSize: '1.1rem', fontWeight: 800 }}>
              Preferencias de cookies
            </h2>
            <p style={{ color: '#a1a1aa', fontSize: '0.82rem', lineHeight: 1.55, marginTop: 8, marginBottom: 20 }}>
              Elige qué tipo de cookies quieres permitir. Puedes cambiar esto
              después desde la página de privacidad.
            </p>

            {/* Cookies esenciales — siempre on, no se pueden desactivar */}
            <div style={{
              padding: '12px 14px',
              background: 'rgba(34, 197, 94, 0.06)',
              border: '1px solid rgba(34, 197, 94, 0.2)',
              borderRadius: 10,
              marginBottom: 10,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                <div style={{ color: '#86efac', fontSize: '0.85rem', fontWeight: 700 }}>
                  Cookies esenciales
                </div>
                <span style={{ color: '#86efac', fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase' }}>
                  Siempre activas
                </span>
              </div>
              <div style={{ color: '#a1a1aa', fontSize: '0.74rem', lineHeight: 1.5 }}>
                Necesarias para mantener tu sesión segura. Sin estas no podrías
                iniciar sesión ni operar la app.
              </div>
            </div>

            {/* Cookies de marketing — toggle */}
            <label
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 12,
                padding: '12px 14px',
                background: 'rgba(255, 255, 255, 0.03)',
                border: `1px solid ${marketingPref ? 'rgba(184, 134, 11, 0.4)' : 'rgba(255, 255, 255, 0.08)'}`,
                borderRadius: 10,
                marginBottom: 20,
                cursor: 'pointer',
              }}
            >
              <input
                type="checkbox"
                checked={marketingPref}
                onChange={(e) => setMarketingPref(e.target.checked)}
                aria-label="Permitir cookies de marketing y analíticas"
                style={{ width: 18, height: 18, accentColor: '#D4AF37', cursor: 'pointer', marginTop: 2, flexShrink: 0 }}
              />
              <div style={{ flex: 1 }}>
                <div style={{ color: '#e4e4e7', fontSize: '0.85rem', fontWeight: 700, marginBottom: 4 }}>
                  Cookies de marketing y analíticas
                </div>
                <div style={{ color: '#a1a1aa', fontSize: '0.74rem', lineHeight: 1.5 }}>
                  Meta Pixel, TikTok Pixel, Google Analytics. Nos dejan saber qué Reels
                  y anuncios funcionan. No se comparte info personal — solo eventos
                  anónimos como "alguien hizo signup desde este Reel".
                </div>
              </div>
            </label>

            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => setShowCustomize(false)} style={btnGhost}>
                Cancelar
              </button>
              <button onClick={savePreference} style={btnPrimary}>
                Guardar preferencias
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// Mismo tamaño = misma prominencia visual = GDPR-safe.
const btnBase = {
  flex: 1,
  padding: '10px 16px',
  borderRadius: 8,
  fontWeight: 700,
  fontSize: '0.82rem',
  cursor: 'pointer',
  fontFamily: 'inherit',
  textAlign: 'center',
  transition: 'opacity 0.15s, transform 0.05s',
};

const btnGhost = {
  ...btnBase,
  background: 'transparent',
  color: '#e4e4e7',
  border: '1px solid rgba(255, 255, 255, 0.18)',
};

const btnPrimary = {
  ...btnBase,
  background: 'linear-gradient(135deg, #D4AF37 0%, #B8860B 100%)',
  color: '#000',
  border: 'none',
  fontWeight: 800,
};
